import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Loader2, User, Mail, Phone as PhoneIcon, FileText } from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { PhoneInput } from '@/components/ui/phone-input';
import { cn } from '@/lib/utils';
import { adminBookingService } from '@/services/api/admin-booking.service';
import type { Booking } from '@/types/booking';

const editBookingSchema = z.object({
  customer_first_name: z.string().min(1, 'First name is required'),
  customer_last_name: z.string().min(1, 'Last name is required'),
  customer_email: z.string().email('Invalid email format'),
  customer_phone: z.string()
    .optional()
    .or(z.literal(''))
    .refine(
      (val) => !val || val === '' || isValidPhoneNumber(val),
      'Please enter a valid phone number'
    ),
  notes: z.string().optional(),
});

type EditBookingFormData = z.infer<typeof editBookingSchema>;

interface EditBookingDialogProps {
  booking: Booking;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string | null;
}

export function EditBookingDialog({ booking, open, onOpenChange, bookingId }: EditBookingDialogProps) {
  const { t } = useTranslation(['bookings', 'common']);
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditBookingFormData>({
    resolver: zodResolver(editBookingSchema),
    defaultValues: {
      customer_first_name: booking.customerFirstName || '',
      customer_last_name: booking.customerLastName || '',
      customer_email: booking.customerEmail || '',
      customer_phone: booking.customerPhone || '',
      notes: booking.notes || '',
    },
  });

  // Reset form when booking changes or dialog opens
  useEffect(() => {
    if (open) {
      reset({
        customer_first_name: booking.customerFirstName || '',
        customer_last_name: booking.customerLastName || '',
        customer_email: booking.customerEmail || '',
        customer_phone: booking.customerPhone || '',
        notes: booking.notes || '',
      });
    }
  }, [open, booking, reset]);

  const updateMutation = useMutation({
    mutationFn: (data: EditBookingFormData) =>
      adminBookingService.updateBooking(booking.public_id || booking.publicId || booking.id, data),
    onSuccess: () => {
      toast.success(t('bookings:toasts.updateSuccess', 'Booking updated successfully'));
      queryClient.invalidateQueries({ queryKey: ['admin-booking', bookingId] });
      queryClient.invalidateQueries({ queryKey: ['admin-bookings'] });
      onOpenChange(false);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || error?.message || 'Failed to update booking';
      toast.error(message);
    },
  });

  const onSubmit = (data: EditBookingFormData) => {
    updateMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('bookings:edit.title', 'Edit Booking Details')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* First Name */}
            <div className="space-y-2">
              <Label htmlFor="edit_first_name" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {t('bookings:edit.firstName', 'First Name')} <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="customer_first_name"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="edit_first_name"
                    className={cn(errors.customer_first_name && 'border-red-500')}
                  />
                )}
              />
              {errors.customer_first_name && (
                <p className="text-sm text-red-500">{errors.customer_first_name.message}</p>
              )}
            </div>

            {/* Last Name */}
            <div className="space-y-2">
              <Label htmlFor="edit_last_name" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                {t('bookings:edit.lastName', 'Last Name')} <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="customer_last_name"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="edit_last_name"
                    className={cn(errors.customer_last_name && 'border-red-500')}
                  />
                )}
              />
              {errors.customer_last_name && (
                <p className="text-sm text-red-500">{errors.customer_last_name.message}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="edit_email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                {t('bookings:edit.email', 'Email')} <span className="text-red-500">*</span>
              </Label>
              <Controller
                name="customer_email"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="edit_email"
                    type="email"
                    className={cn(errors.customer_email && 'border-red-500')}
                  />
                )}
              />
              {errors.customer_email && (
                <p className="text-sm text-red-500">{errors.customer_email.message}</p>
              )}
            </div>

            {/* Phone */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="edit_phone" className="flex items-center gap-2">
                <PhoneIcon className="h-4 w-4" />
                {t('bookings:edit.phone', 'Phone (optional)')}
              </Label>
              <Controller
                name="customer_phone"
                control={control}
                render={({ field }) => (
                  <PhoneInput
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="+1 (555) 000-0000"
                  />
                )}
              />
              {errors.customer_phone && (
                <p className="text-sm text-red-500">{errors.customer_phone.message}</p>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="edit_notes" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                {t('bookings:edit.notes', 'Notes (optional)')}
              </Label>
              <Controller
                name="notes"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    id="edit_notes"
                    rows={3}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common:actions.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('bookings:edit.saving', 'Saving...')}
                </>
              ) : (
                t('bookings:edit.save', 'Save Changes')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
